/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: get back to fixing this at some point
import {
    ALL_COMBAT_STATS,
    ALL_COMBAT_SUB_STATS,
    ALL_JOBS,
    CURRENT_MAX_LEVEL,
    defaultItemDisplaySettings,
    DefaultMateriaFillPrio,
    DOH_STATS,
    DOL_STATS,
    getClassJobStats,
    getDefaultDisplaySettings,
    getRaceStats,
    JOB_DATA,
    JobName,
    LEVEL_ITEMS,
    MAIN_STATS,
    DEFAULT_MATERIA_ACCEPTABLE_OVERCAP_LOSS,
    MateriaSubstat,
    RaceName,
    SPECIAL_STAT_KEYS,
    SPECIAL_STATS_MAPPING,
    SpecialStatKey,
    SupportedLevel
} from "@xivgear/xivmath/xivconstants";
import {
    ComputedSetStats,
    ComputedSetStatsExport,
    DisplayGearSlotKey,
    EquippedItem,
    EquipSlotKey,
    EquipSlots,
    FoodItem,
    GearItem,
    IlvlSyncInfo,
    ItemDisplaySettings,
    ItemSlotExport,
    JobData,
    JobDataConst,
    JobDataExport,
    Materia,
    MedicineItem,
    MateriaAutoFillController,
    MateriaAutoFillPrio,
    MateriaFillMode,
    MateriaSlot,
    MateriaWasteLimits,
    MeldableMateriaSlot,
    NormalOccGearSlotKey,
    OccGearSlotKey,
    PartyBonusAmount,
    RawStatKey,
    SetExport,
    SetExportExternalSingle,
    SetStatsExport,
    SheetExport,
    SheetStatsExport,
    SimExport,
    Substat
} from "@xivgear/xivmath/geartypes";
import {CharacterGearSet, SyncInfo} from "./gear";
import {DataManager, DmJobs, makeDataManager} from "./datamanager";
import {Inactivitytimer} from "@xivgear/util/inactivitytimer";
import {writeProxy} from "@xivgear/util/proxies";
import {SimCurrentResult, SimResult, Simulation} from "./sims/sim_types";
import {getDefaultSims, getRegisteredSimSpecs, getSimSpecByStub} from "./sims/sim_registry";
import {DUMMY_SHEET_MGR, SheetManager} from "./persistence/saved_sheets";
import {CustomItem} from "./customgear/custom_item";
import {CustomFood} from "./customgear/custom_food";
import {isMateriaAllowed, materiaShortLabel} from "./materia/materia_utils";
import {inflateSetExport} from "./util/sheet_utils";
import {SpecialStatType} from "@xivgear/data-api-client/dataapi";

export type SheetCtorArgs = ConstructorParameters<typeof GearPlanSheet>
export type SheetContstructor<SheetType extends GearPlanSheet> = (...values: SheetCtorArgs) => SheetType;

/**
 * SheetProvider is the base class for turning sheet exports or fresh sheets into rehydrated sheet objects.
 */
export class SheetProvider<SheetType extends GearPlanSheet> {

    constructor(private readonly sheetConstructor: SheetContstructor<SheetType>, private readonly sheetManager: SheetManager) {
    }

    private construct(...args: SheetCtorArgs): SheetType {
        return this.sheetConstructor(...args);
    }

    /**
     * Produce a sheet from a SheetExport
     *
     * @param importedData
     */
    fromExport(importedData: SheetExport): SheetType {
        const sheet = this.construct(undefined, importedData, this.sheetManager);
        // If sims are not specified at all in the import, we want to add the
        // defaults.
        // Note that this will not add sims if they are specified as [], only
        // if unspecified.
        // We check the import data here as the sheet will have sims = [] at this
        // point.
        if (importedData.sims === undefined) {
            sheet.shouldAddDefaultSimsToNewSheet = true;
        }
        return sheet;
    }

    /**
     * Produce a sheet from a single-set export.
     *
     * @param importedData
     */
    fromSetExport(...importedData: SetExportExternalSingle[]): SheetType {
        const gearPlanSheet = this.fromExport(inflateSetExport(...importedData));
        if (importedData[0].sims === undefined) {
            gearPlanSheet.shouldAddDefaultSimsToNewSheet = true;
        }
        // TODO
        // gearPlanSheet._selectFirstRowByDefault = true;
        return gearPlanSheet;
    }

    /**
     * Create a new sheet from scratch
     *
     * @param sheetKey The save key for the sheet
     * @param sheetName The name of the sheet
     * @param classJob The class/job of the sheet
     * @param level The level of the sheet
     * @param ilvlSync The ilvl sync of the sheet, or undefined if the sheet should not have an ilvl sync.
     * @param multiJob Whether to create a multi-job sheet.
     */
    fromScratch(sheetKey: string, sheetName: string, classJob: JobName, level: SupportedLevel, ilvlSync: number | undefined, multiJob: boolean): SheetType {
        const defaultPartyBonus: PartyBonusAmount = JOB_DATA[classJob]?.defaultPartyBonus ?? 5;
        const fakeExport: SheetExport = {
            job: classJob,
            level: level,
            name: sheetName,
            partyBonus: defaultPartyBonus,
            race: undefined,
            saveKey: sheetKey,
            sets: [{
                name: "Default Set",
                items: {},
            }],
            sims: [],
            ilvlSync: ilvlSync,
            isMultiJob: multiJob,
            // ctor will auto-fill the rest
        };
        const gearPlanSheet = this.construct(sheetKey, fakeExport, this.sheetManager);
        gearPlanSheet.shouldAddDefaultSimsToNewSheet = true;
        return gearPlanSheet;
    }

    /**
     * Try to load a sheet by its save key
     *
     * @param sheetKey The key to load
     * @returns The sheet if found, otherwise null
     */
    fromSaved(sheetKey: string): SheetType | null {
        const handle = this.sheetManager.getByKey(sheetKey);
        const data = handle?.dataNow;
        if (data) {
            return this.construct(sheetKey, handle.dataNow, this.sheetManager);
        }
        return null;
    }
}

export const HEADLESS_SHEET_PROVIDER = new SheetProvider((...args) => new GearPlanSheet(...args), DUMMY_SHEET_MGR);


// TODO: core shouldn't reference localStorage

/**
 * Base class for a sheet. For graphical usage, see GearPlanSheetGui.
 */
export class GearPlanSheet {
    // General sheet properties
    private _sheetName: string;
    private _description: string | undefined;
    classJobName: JobName;
    readonly altJobs: JobName[];
    isMultiJob: boolean;
    level: SupportedLevel;
    ilvlSync: number | undefined;
    private _race: RaceName | undefined;
    private _partyBonus: PartyBonusAmount;
    private readonly _saveKey: string | undefined;
    private readonly _importedData: SheetExport;
    shouldAddDefaultSimsToNewSheet: boolean = false;

    // Sheet data
    private _sets: CharacterGearSet[] = [];
    private _sims: Simulation<any, any, any>[] = [];

    // Data helpers
    private dataManager!: DataManager;
    private _relevantMateria!: Materia[];
    private _dmRelevantFood!: FoodItem[];

    // Custom items
    private _customItems: CustomItem[] = [];
    private _customFoods: CustomFood[] = [];

    // Materia autofill
    protected materiaAutoFillPrio: MateriaAutoFillPrio;
    // protected materiaAutoFillSelectedItems: boolean;
    protected materiaFillMode: MateriaFillMode;

    // Display settings
    private _showAdvancedStats: boolean = false;
    private readonly _itemDisplaySettings: ItemDisplaySettings = getDefaultDisplaySettings(CURRENT_MAX_LEVEL, 'WHM', undefined);

    // Init related
    private _setupDone: boolean = false;
    private saveEnabled: boolean = false;

    // Display state
    private _isViewOnly: boolean = false;
    private _isSpecificItems: boolean = false;
    isEmbed: boolean = false;

    // Temporal state
    private readonly saveTimer: Inactivitytimer;

    protected _timestamp: Date;

    // Occult Crescent et al
    private _activeSpecialStat: SpecialStatType | null = null;

    protected sheetManager: SheetManager;

    // Set of item IDs which are hidden
    private hiddenItems: Set<number> = new Set<number>();

    // Can't make ctor private for custom element, but DO NOT call this directly - use fromSaved or fromScratch
    constructor(sheetKey: string | undefined, importedData: SheetExport, manager: SheetManager) {
        console.log(importedData);
        this.sheetManager = manager;
        this._importedData = importedData;
        this._saveKey = sheetKey;
        this._sheetName = importedData.name;
        this.level = importedData.level ?? CURRENT_MAX_LEVEL;
        this._race = importedData.race;
        this._partyBonus = importedData.partyBonus ?? 0;
        // TODO: why does this default to WHM? Shouldn't it just throw?
        this.classJobName = importedData.job ?? 'WHM';
        this.isMultiJob = importedData.isMultiJob ?? false;
        this._activeSpecialStat = (importedData.specialStats ?? null) as SpecialStatType | null;
        this.altJobs = this.isMultiJob ? [
            ...ALL_JOBS.filter(job => {
                const thatJobData = JOB_DATA[job];
                const thisJobData = JOB_DATA[this.classJobName];
                return thatJobData.type === thisJobData.type && thatJobData.combatRole === thisJobData.combatRole
                    // Don't include the primary job in the list of alt jobs
                    && job !== this.classJobName;
            }),
        ] : [];
        this.ilvlSync = importedData.ilvlSync;
        this._description = importedData.description;
        const defaults = getDefaultDisplaySettings(this.level, this.classJobName, this.ilvlSync);
        if (importedData.itemDisplaySettings) {
            Object.assign(this._itemDisplaySettings, importedData.itemDisplaySettings);
        }
        else {
            Object.assign(this._itemDisplaySettings, defaults);
        }
        if (importedData.hiddenItems) {
            importedData.hiddenItems.forEach(itemId => this.hiddenItems.add(itemId));
        }
        else {
            // if the import does not include hidden item info, reset this to the default.
            this._itemDisplaySettings.showHidden = defaults.showHidden;
        }
        const maxWaste: MateriaWasteLimits = {...(importedData.mfMaxWaste ?? {})};
        this.materiaAutoFillPrio = {
            statPrio: importedData.mfp ?? [...DefaultMateriaFillPrio.filter(stat => this.isStatRelevant(stat))],
            // Just picking a bogus value so the user understands what it is
            minGcd: importedData.mfMinGcd ?? 2.05,
            maxWaste: maxWaste,
        };
        this.materiaFillMode = importedData.mfm ?? 'retain_item';

        if (importedData.customItems) {
            importedData.customItems.forEach(ci => {
                this._customItems.push(CustomItem.fromExport(ci, this));
            });
        }
        if (importedData.customFoods) {
            importedData.customFoods.forEach(ci => {
                this._customFoods.push(CustomFood.fromExport(ci));
            });
        }
        if (importedData.timestamp) {
            this._timestamp = new Date(importedData.timestamp);
        }
        else {
            this._timestamp = new Date();
        }

        // Early gui setup
        this.saveTimer = new Inactivitytimer(1_000, () => this.saveData());
    }

    /**
     * The key under which this sheet should be saved in LocalStorage.
     */
    get saveKey() {
        return this._saveKey;
    }

    get metaSaveKey() {
        return this._saveKey + '-meta';
    }

    /**
     * Whether to show advanced stats.
     */
    get showAdvancedStats() {
        return this._showAdvancedStats;
    }

    set showAdvancedStats(show: boolean) {
        this._showAdvancedStats = show;
    }

    /**
     * Whether setup/loading is done.
     */
    get setupDone() {
        return this._setupDone;
    }

    /**
     * Whether this sheet is in view-only mode.
     */
    get isViewOnly() {
        return this._isViewOnly;
    }

    /**
     * The materia autofill controller for this sheet.
     *
     * This only has a real implementation in GearPlanSheetGui, since materia autofilling is a gui-only feature.
     */
    get materiaAutoFillController(): MateriaAutoFillController {
        const outer = this;
        return {
            get autoFillMode() {
                return outer.materiaFillMode;
            },
            set autoFillMode(mode: MateriaFillMode) {
                outer.materiaFillMode = mode;
                outer.requestSave();
            },
            get prio() {
                return writeProxy<MateriaAutoFillPrio>(outer.materiaAutoFillPrio, () => outer.requestSave());
            },
            callback(): void {
                outer.requestSave();
            },
            fillAll(): void {
                console.log('fillAll not implemented');
            },
            fillEmpty(): void {
                console.log('fillEmpty not implemented');
            },
            lockEmpty(): void {
                console.log('lockEmpty not implemented');
            },
            lockFilled(): void {
                console.log('lockFilled not implemented');
            },
            unequipUnlocked(): void {
                console.log('unequipUnlocked not implemented');
            },
            unlockAll(): void {
                console.log('unlockAll not implemented');
            },

        };
    }

    /**
     * Enable view-only mode. Cannot be disabled once enabled.
     */
    setViewOnly() {
        this._isViewOnly = true;
    }

    /**
     * Enable view-only mode, and specifically only request the items in the exported data from the DataManager.
     * This should be used by the stats server's fulldata endpoints, but the jury's still out on client-side usage, as
     * the smaller response has to be weighed against the significantly lower
     *
     */
    setSpecificItemsView() {
        this._isViewOnly = true;
        this._isSpecificItems = true;
    }

    get allJobs(): DmJobs {
        return [this.classJobName, ...this.altJobs];
    }

    /**
     * Load the sheet data fully. Create a DataManager internally.
     */
    async load() {
        let specificItems: Set<number> | undefined;
        if (this._isSpecificItems) {
            specificItems = new Set<number>();
            this._importedData.sets.forEach(set => {
                for (const equipmentSlot in set.items) {
                    const importedItem: ItemSlotExport | undefined = set.items[equipmentSlot as EquipSlotKey];
                    if (importedItem) {
                        specificItems.add(importedItem.id);
                    }
                }
            });
        }
        else {
            specificItems = undefined;
        }
        const dataManager = makeDataManager(this.allJobs, this.level, this.ilvlSync, specificItems);
        await dataManager.loadData();
        await this.loadFromDataManager(dataManager);

        if (this.shouldAddDefaultSimsToNewSheet) {
            this.addDefaultSims();
            this.shouldAddDefaultSimsToNewSheet = false;
        }
    }

    /**
     * Load the sheet data from a DataManager. The DataManager must already be loaded before calling this method.
     * This can be used for things like meld solving, where a worker can load a DM once and then re-use it.
     *
     * @param dataManager The DataManager to use.
     */
    async loadFromDataManager(dataManager: DataManager) {
        console.log("Reading data");
        const saved = this._importedData;
        const lvlItemInfo = LEVEL_ITEMS[this.level];
        this.dataManager = dataManager;
        this._relevantMateria = this.dataManager.allMateria.filter(mat => {
            return mat.materiaGrade <= lvlItemInfo.maxMateria
                // && mat.materiaGrade >= lvlItemInfo.minMateria
                && this.isStatRelevant(mat.primaryStat);
        });
        this._relevantMateria.forEach(mat => {
            const stat = mat.primaryStat;
            if (stat in this.materiaAutoFillPrio.maxWaste) {
                return;
            }
            this.materiaAutoFillPrio.maxWaste[stat] = DEFAULT_MATERIA_ACCEPTABLE_OVERCAP_LOSS;
        });
        this.recheckCustomItems();
        for (const importedSet of saved.sets) {
            this.addGearSet(this.importGearSet(importedSet));
        }
        // If this is an embed, we don't care about sims.
        if (saved.sims && !this.isEmbed) {
            for (const simport of saved.sims) {
                const simSpec = getSimSpecByStub(simport.stub);
                if (simSpec === undefined) {
                    console.error("Sim no longer present: " + simport.stub);
                    continue;
                }
                try {
                    const rehydratedSim: Simulation<any, any, any> = simSpec.loadSavedSimInstance(simport.settings);
                    if (simport.name) {
                        rehydratedSim.displayName = simport.name;
                    }
                    if (rehydratedSim.settings.includeInExport === undefined) {
                        rehydratedSim.settings.includeInExport = true;
                    }
                    this.addSim(rehydratedSim);
                }
                catch (e) {
                    console.error("Error loading sim settings", e);
                }
            }
        }
        this._dmRelevantFood = this.dataManager.allFoodItems.filter(food => this.isStatRelevant(food.primarySubStat) || this.isStatRelevant(food.secondarySubStat));
        // This is set when importing - but it needs to know about items first. Thus we have to redo this now.
        this.activeSpecialStat = this._activeSpecialStat;
        this._setupDone = true;
        setTimeout(() => {
            this.saveEnabled = true;
        }, 500);
    }

    /**
     * Save data for this sheet now.
     */
    saveData() {
        if (!this.saveEnabled) {
            // Don't clobber a save with empty data because the sheet hasn't loaded!
            return;
        }
        if (this.saveKey) {
            console.log("Saving sheet " + this.sheetName);
            this._timestamp = new Date();
            this.sheetManager.saveData(this);
        }
        else {
            console.debug("Ignoring request to save sheet because it has no save key");
        }
    }

    /**
     * Request a save to be completed asynchronously. The actual save is only performed once no more requestSave() calls
     * have happened for a certain timeout, thus allowing multiple modifications to be coalesced into a single save
     * operation.
     */
    requestSave(): void {
        // We want saving to be temporarily suppressed prior to the sheet fully loading, so that we don't get a useless
        // save if you open a sheet and don't change anything.
        if (!this.saveEnabled) {
            return;
        }
        this.saveTimer.ping();
    }

    /**
     * The name of the sheet.
     */
    get sheetName() {
        return this._sheetName;
    }

    set sheetName(name: string) {
        this._sheetName = name;
        this.requestSave();
    }


    /**
     * The description of the sheet.
     */
    get description(): string | undefined {
        return this._description;
    }

    set description(desc: string) {
        this._description = desc;
        this.requestSave();
    }


    /**
     * Copy this sheet to a new save slot.
     *
     * @param name New name. Leave unspecified/undefined to keep existing name.
     * @param job New job. Leave unspecified/undefined to keep existing job.
     * @param level New level. Leave unspecified/undefined to keep existing level.
     * @param ilvlSync New ilvl sync. Leave unspecified or use special value 'keep' to keep existing ilvl sync.
     * @param multiJob Whether to create the new sheet as a multi-job sheet or not.
     * @returns The saveKey of the new sheet.
     */
    saveAs(name: string, job: JobName, level: SupportedLevel, ilvlSync: number | 'keep' | undefined, multiJob: boolean): string {
        const exported = this.exportSheet(ExportTypes.InternalSaveAs);
        if (name !== undefined) {
            exported.name = name;
        }
        if (job !== undefined) {
            exported.job = job;
        }
        if (level !== undefined) {
            exported.level = level;
        }
        if (ilvlSync !== 'keep') {
            exported.ilvlSync = ilvlSync;
        }
        exported.isMultiJob = multiJob;
        if (!multiJob) {
            exported.sets.forEach(setExport => {
                setExport.jobOverride = null;
            });
        }
        return this.sheetManager.saveAs(exported);
    }


    exportSims(mode: SimExportMode): SimExport[] {
        if (mode === 'none') {
            return [];
        }
        return this._sims.filter(sim => {
            return mode === 'all' || sim.settings.includeInExport === true;
        }).map(sim =>
            ({
                stub: sim.spec.stub,
                settings: sim.exportSettings(),
                name: sim.displayName,
            }));
    }

    /**
     * Export the sheet to serialized form.
     *
     * @param opts Options for the export.
     */
    exportSheet<X extends SheetExportOptions>(opts: X): ExportTypeFor<X> {
        const sets: ExportTypeFor<X>['sets'] = this._sets.map(set => {
            const rawExport = this.exportGearSet(set, false);
            if (opts.includeStats) {
                const augGs: SetStatsExport = {
                    ...rawExport,
                    computedStats: statsSerializationProxy(set.computedStats),
                };
                return augGs;
            }
            return rawExport;
        });
        const simsExport: SimExport[] = this.exportSims(opts.includeSims);
        const out: SheetExport = {
            name: this.sheetName,
            sets: sets,
            level: this.level,
            job: this.classJobName,
            partyBonus: this._partyBonus,
            race: this._race,
            sims: simsExport,
            itemDisplaySettings: this._itemDisplaySettings,
            mfm: this.materiaFillMode,
            mfp: this.materiaAutoFillPrio.statPrio,
            mfMinGcd: this.materiaAutoFillPrio.minGcd,
            mfMaxWaste: this.materiaAutoFillPrio.maxWaste,
            ilvlSync: this.ilvlSync,
            description: this.description,
            customItems: this._customItems.map(ci => ci.export()),
            customFoods: this._customFoods.map(cf => cf.export()),
            timestamp: this.timestamp.getTime(),
            isMultiJob: this.isMultiJob,
            specialStats: this.activeSpecialStat ?? null,
        };
        if (opts.includeSaveKey) {
            out.saveKey = this._saveKey;
        }
        if (opts.includeItemFlags) {
            out.hiddenItems = Array.from(this.hiddenItems);
        }
        // @ts-expect-error Don't know how to make it work - the only issue is that 'sets' is the wrong type.
        return out;

    }

    /**
     * Add a new gear set.
     *
     * @param gearSet The set to add.
     * @param index The index at which to insert the set. If omitted, it is added to the end.
     */
    addGearSet(gearSet: CharacterGearSet, index?: number) {
        if (index === undefined) {
            this._sets.push(gearSet);
        }
        else {
            this._sets.splice(index, 0, gearSet);
        }
        gearSet.addListener(() => {
            this.requestSave();
        });
        this.saveData();
    }

    /**
     * Delete a gear set.
     *
     * @param gearSet
     */
    delGearSet(gearSet: CharacterGearSet) {
        this._sets = this._sets.filter(gs => gs !== gearSet);
        this.saveData();
    }

    /**
     * Move a gear set to a new index.
     *
     * @param gearSet The set to move.
     * @param to      The index to which the gearset should be re-ordered.
     */
    reorderSet(gearSet: CharacterGearSet, to: number) {
        const sets = [...this._sets];
        const from = sets.indexOf(gearSet);
        if (from === to) {
            return;
        }
        if (from < 0 || to < 0) {
            return;
        }
        const removed = sets.splice(from, 1)[0];
        sets.splice(to, 0, removed);
        this._sets = sets;
    }

    /**
     * Clones the given gear set and adds it as a new gear set.
     *
     * This will add it prior to the next separator, since separators are typically used to delineate categories
     * of sets.
     *
     * @param gearSet
     */
    cloneAndAddGearSet(gearSet: CharacterGearSet) {
        const cloned = this.importGearSet(this.exportGearSet(gearSet));
        cloned.name += ' copy';
        const toIndex: number | undefined = this.clonedSetPlacement(gearSet);
        this.addGearSet(cloned, toIndex);
    }

    /**
     * Determine the placement index for a cloned set. Helper method of {@link cloneAndAddGearSet}.
     *
     * @param originalSet The set being cloned.
     * @protected
     */
    protected clonedSetPlacement(originalSet: CharacterGearSet): number | undefined {
        const originalIndex = this.sets.indexOf(originalSet);
        if (originalIndex >= 0) {
            for (let i = originalIndex + 1; i < this.sets.length; i++) {
                if (this.sets[i].isSeparator) {
                    return i;
                }
            }
        }
        // fall back
        return undefined;

    }

    exportGearSet(set: CharacterGearSet): SetExport;
    exportGearSet(set: CharacterGearSet, external: false): SetExport;
    exportGearSet(set: CharacterGearSet, external: true): SetExportExternalSingle;
    /**
     * Export a CharacterGearSet to a SetExport so that it can safely be serialized for saving or sharing.
     *
     * @param set The set to export.
     * @param standalone true to create a single-set top-level export, i.e. include fields that would normally be at the
     * sheet level, such as job and sims.
     */
    exportGearSet(set: CharacterGearSet, standalone: boolean = false): SetExport | SetExportExternalSingle {
        const items: { [K in EquipSlotKey]?: ItemSlotExport } = {};
        for (const k in set.equipment) {
            const equipmentKey = k as EquipSlotKey;
            const inSlot: EquippedItem | null = set.equipment[equipmentKey];
            if (inSlot) {
                const exportedItem: ItemSlotExport = {
                    // TODO: determine if it makes more sense to just serialize empty materia slots as {}
                    // The advantage is that {} is a lot smaller than {"id":-1}, and exports are already long
                    // On the other hand, *most* real exports would have slots filled (BiS etc)
                    id: inSlot.gearItem.id,
                    materia: inSlot.melds.map(meld => {
                        return {
                            id: meld.equippedMateria?.id ?? -1,
                            locked: meld.locked,
                        };
                    }),
                };
                if (inSlot.relicStats && Object.entries(inSlot.relicStats)) {
                    exportedItem.relicStats = {...inSlot.relicStats};
                }
                if (inSlot.gearItem.isNqVersion) {
                    exportedItem.forceNq = true;
                }
                items[equipmentKey] = exportedItem;
            }
        }
        const out: SetExport = {
            name: set.name,
            items: items,
            food: set.food ? set.food.id : undefined,
            medicine: set.medicine ? set.medicine.id : undefined,
            description: set.description,
            isSeparator: set.isSeparator,
        };
        if (standalone) {
            const ext = out as SetExportExternalSingle;
            ext.job = this.classJobName;
            ext.level = this.level;
            ext.ilvlSync = this.ilvlSync;
            ext.sims = this.exportSims('all'); // TODO
            ext.customItems = this._customItems.map(ci => ci.export());
            ext.customFoods = this._customFoods.map(cf => cf.export());
            ext.partyBonus = this._partyBonus;
            ext.race = this._race;
            ext.job = set.job;
            ext.specialStats = this.activeSpecialStat;
        }
        else {
            if (set.relicStatMemory) {
                out.relicStatMemory = set.relicStatMemory.export();
            }
            if (set.materiaMemory) {
                out.materiaMemory = set.materiaMemory.export();
            }
            if (this.isMultiJob && set.jobOverride) {
                out.jobOverride = set.jobOverride;
            }
        }
        return out;
    }

    /**
     * Return an item from the DataManager by its ID. Returns undefined if the item could not be found.
     *
     * @param id The item ID. For custom items, use the custom item's fake ID.
     * @param forceNq If true, searches for an NQ version of an otherwise-HQ item.
     */
    itemById(id: number, forceNq: boolean = false): GearItem | undefined {
        const custom = this._customItems.find(ci => ci.id === id);
        if (custom) {
            return custom;
        }
        else {
            return this.dataManager.itemById(id, forceNq);
        }
    }

    /**
     * Return a food item from the DataManager by its ID. Returns undefined if the item could not be found.
     *
     * @param id The item ID. For custom items, use the custom item's fake ID.
     */
    foodById(id: number): FoodItem | undefined {
        const custom = this._customFoods.find(cf => cf.id === id);
        if (custom) {
            return custom;
        }
        else {
            return this.dataManager.foodById(id);
        }
    }

    /**
     * Return a medicine item from the DataManager by its ID. Returns undefined if the item could not be found.
     *
     * @param id
     */
    medicineById(id: number): MedicineItem | undefined {
        return this.dataManager.medicineById(id);
    }

    /**
     * Return the ilvl sync info for a particular level.
     *
     * @param ilvl
     */
    ilvlSyncInfo(ilvl: number): IlvlSyncInfo | undefined {
        return this.dataManager?.getIlvlSyncInfo(ilvl);
    }

    /**
     * Return the highest ilvl of non-custom items in a particular slot
     *
     * @param slot
     */
    highestIlvlForSlot(slot: OccGearSlotKey): number {
        return Math.max(...this.dataManager.allItems.filter(item => item.occGearSlotName === slot)
            .map(item => item.ilvl));
    }

    /**
     * Returns the highest
     * @param slot
     */
    highestIlvlItemForSlot(slot: OccGearSlotKey): GearItem | undefined {
        return this.dataManager.allItems.filter(item => item.occGearSlotName === slot)
            .sort((a, b) => b.ilvl - a.ilvl)[0];
    }

    /**
     * Get the next free custom item ID.
     * @private
     */
    private get nextCustomItemId() {
        if (this._customItems.length === 0) {
            return 10_000_000_000_000 + Math.floor(Math.random() * 1_000_000);
        }
        else {
            return Math.max(...this._customItems.map(ci => ci.id)) + 1;
        }
    }

    /**
     * Returns the starting set of data for a new custom item.
     * @param slot The slot for which to make the custom item.
     */
    newCustomItem(slot: NormalOccGearSlotKey): CustomItem {
        const item = CustomItem.fromScratch(this.nextCustomItemId, slot, this);
        this._customItems.push(item);
        this.recheckCustomItems();
        this.requestSave();
        return item;
    }

    /**
     * Returns the starting set of data for a new custom food.
     */
    newCustomFood(): CustomFood {
        const food = CustomFood.fromScratch(this.nextCustomItemId);
        this._customFoods.push(food);
        this.requestSave();
        return food;
    }

    /**
     * All custom items in this sheet.
     */
    get customItems() {
        return [...this._customItems];
    }

    /**
     * All custom food items in this sheet.
     */
    get customFood() {
        return [...this._customFoods];
    }

    /**
     * Delete the given custom item, first asking for confirmation if the item is equipped on any set.
     *
     * @param item The item to delete.
     * @param confirmationCallback A callback to be called to confirm the deletion of an item which is currently
     * equipped. Receives the names of sets as the first parameter.
     */
    deleteCustomItem(item: CustomItem, confirmationCallback: (equipppedOnsets: string[]) => boolean): boolean {
        const setsWithThisItem = this.sets.filter(set => set.allEquippedItems.includes(item));
        if (setsWithThisItem.length > 0) {
            const confirmed = confirmationCallback(setsWithThisItem.map(set => set.name));
            if (!confirmed) {
                return false;
            }
        }
        this.forceDeleteCustomItem(item);
        return true;
    }

    /**
     * Delete a custom item without confirmation. Will unequip the item from any sets which currently use it.
     *
     * @param item The item to delete.
     */
    forceDeleteCustomItem(item: CustomItem) {
        // Unequip existing
        this.sets.forEach(set => {
            EquipSlots.forEach(slot => {
                if (set.getItemInSlot(slot) === item) {
                    set.setEquip(slot, null);
                }
            });
        });
        const idx = this._customItems.indexOf(item);
        if (idx > -1) {
            this._customItems.splice(idx, 1);
        }
        this.recheckCustomItems();
        this.recalcAll();
    }

    /**
     * Delete the given custom food item, first asking for confirmation if the item is equipped on any set.
     *
     * @param item The food item to delete
     * @param confirmationCallback A callback to be called to confirm the deletion of an item which is currently
     * equipped. Receives the names of sets as the first parameter.
     */
    deleteCustomFood(item: CustomFood, confirmationCallback: (equipppedOnsets: string[]) => boolean) {
        const setsWithThisItem = this.sets.filter(set => set.food === item);
        if (setsWithThisItem.length > 0) {
            const confirmed = confirmationCallback(setsWithThisItem.map(set => set.name));
            if (!confirmed) {
                return false;
            }
        }
        this.forceDeleteCustomFood(item);
        return true;
    }

    forceDeleteCustomFood(item: CustomFood) {
        // Unequip existing
        this.sets.forEach(set => {
            if (set.food === item) {
                set.food = undefined;
            }
        });
        const idx = this._customFoods.indexOf(item);
        if (idx > -1) {
            this._customFoods.splice(idx, 1);
        }
        this.recalcAll();
    }

    /**
     * Import a sim from a sim export. Does not add it to the simulations - you still need to do that if you would like
     * the sim to appear on the sheet (see {@link addSim}).
     *
     * @param simExport The data to import.
     */
    importSim(simExport: SimExport): Simulation<any, any, any> | null {
        const simSpec = getSimSpecByStub(simExport.stub);
        if (simSpec === undefined) {
            return null;
        }
        const rehydratedSim = simSpec.loadSavedSimInstance(simExport.settings);
        rehydratedSim.displayName = simExport.name ?? undefined;
        rehydratedSim.settings.includeInExport ??= true;
        return rehydratedSim;
    }

    /**
     * Convert a SetExport back to a CharacterGearSet.
     *
     * This does not add the set to the sheet or do anything other than conversion.
     *
     * @param importedSet
     */
    importGearSet(importedSet: SetExport): CharacterGearSet {
        const set = new CharacterGearSet(this);
        set.name = importedSet.name;
        set.description = importedSet.description;
        if (importedSet.isSeparator) {
            set.isSeparator = true;
        }
        else {

            for (const equipmentSlot in importedSet.items) {
                const importedItem: ItemSlotExport | undefined = importedSet.items[equipmentSlot as EquipSlotKey];
                if (!importedItem) {
                    continue;
                }
                const baseItem = this.itemById(importedItem.id, importedItem.forceNq);
                if (!baseItem) {
                    continue;
                }
                const equipped = new EquippedItem(baseItem);
                for (let i = 0; i < Math.min(equipped.melds.length, importedItem.materia.length); i++) {
                    const importedMateria = importedItem.materia[i];
                    if (importedMateria === undefined) {
                        continue;
                    }
                    const id = importedMateria.id;
                    const mat = this.dataManager.materiaById(id);
                    const slot = equipped.melds[i];
                    if (slot === undefined) {
                        console.error(`mismatched materia slot! i == ${i}, slots length == ${equipped.melds.length}`);
                    }
                    slot.locked = importedMateria.locked ?? false;
                    if (mat) {
                        slot.equippedMateria = mat;
                    }
                }
                if (importedItem.relicStats && equipped.gearItem.isCustomRelic) {
                    Object.assign(equipped.relicStats!, importedItem.relicStats);
                }
                set.equipment[equipmentSlot as EquipSlotKey] = equipped;
            }
            if (importedSet.food) {
                set.food = this.foodById(importedSet.food);
            }
            if (importedSet.medicine) {
                set.medicine = this.medicineById(importedSet.medicine);
            }
            if (importedSet.relicStatMemory) {
                set.relicStatMemory.import(importedSet.relicStatMemory);
            }
            if (importedSet.materiaMemory) {
                set.materiaMemory.import(importedSet.materiaMemory);
            }
            if (importedSet.jobOverride) {
                set.earlySetJobOverride(importedSet.jobOverride);
            }
            // When importing a single set into a multi-job sheet, set the job override
            else if ('job' in importedSet && importedSet.job && this.isMultiJob) {
                set.earlySetJobOverride((importedSet as SetExportExternalSingle).job ?? null);
            }
        }
        return set;
    }

    // TODO: this needs to only update when we have updated that specific gear set, not any random gear set.
    // If this gear set was not updated, then a cached result should be returned.
    getSimResult(simulation: Simulation<any, any, any>, set: CharacterGearSet): SimCurrentResult<SimResult> {
        const simPromise = (async () => {
            // Intentionally de-prioritize this over other tasks so that it doesn't slow down an initial sheet load.
            await new Promise(resolve => setTimeout(resolve, 0));
            return await simulation.simulate(set);
        })();
        const out: SimCurrentResult<any> = {
            result: undefined,
            resultPromise: undefined,
            status: 'Running',
            error: undefined,
        };
        out.resultPromise = new Promise((resolve, reject) => {
            simPromise.then(result => {
                out.status = 'Done';
                out.result = result;
                resolve(out);
            }, error => {
                out.status = 'Error';
                out.error = error;
                console.error("Sim Error!", error);
                reject(error);
            });
        });
        return out;
    }

    /**
     * Force a full recalc of every set. For example, this should be called after a custom item is edited, or
     * after a race/party size change.
     */
    recalcAll() {
        for (const set of this._sets) {
            set.forceRecalc();
        }
    }

    /**
     * Get the class/job stats for the sheet's class/job.
     */
    get classJobStats(): JobData {
        return this.statsForJob(this.classJobName);
    }

    get classJobEarlyStats(): JobDataConst {
        return getClassJobStats(this.classJobName);
    }

    /**
     * Get the class/job stats for a different class/job.
     *
     * @param job
     */
    statsForJob(job: JobName): JobData {
        return {
            ...getClassJobStats(job),
            jobStatMultipliers: this.dataManager.multipliersForJob(job),
        };
    }

    /**
     * Determine whether a stat is relevant to this sheet based on its job.
     *
     * Relevant means:
     * * The stat is not undefined.
     * * If it is the main stat (including vit), then it is the primary stat for the job.
     *    * Vit is not relevant for any job since it is not the main stat of any job.
     * * If it is a substat, it is not filtered out by the job.
     * * If it is gear haste, it is only relevant if we have an active special stat which cares about haste.
     *
     * @param stat
     */
    isStatRelevant(stat: RawStatKey | undefined): boolean {
        if (stat === undefined) {
            return false;
        }
        if (!this.classJobEarlyStats) {
            // Not sure what the best way to handle this is
            return true;
        }
        // TODO
        if (stat === 'vitality') {
            return true;
        }
        switch (this.classJobEarlyStats.type) {
            case "Combat":
                if (DOH_STATS.includes(stat as typeof DOH_STATS[number]) || DOL_STATS.includes(stat as typeof DOL_STATS[number])) {
                    return false;
                }
                if (MAIN_STATS.includes(stat as typeof MAIN_STATS[number])) {
                    return (stat === this.classJobEarlyStats.mainStat);
                }
                if (stat === 'gearHaste') {
                    const specialStat = this.activeSpecialStat;
                    return SPECIAL_STATS_MAPPING[specialStat]?.showHaste ?? false;
                }
                if (this.classJobEarlyStats.irrelevantSubstats) {
                    return !this.classJobEarlyStats.irrelevantSubstats.includes(stat as Substat);
                }
                else {
                    return ALL_COMBAT_STATS.includes(stat as typeof ALL_COMBAT_STATS[number]);
                }
            case "DoH":
                return DOH_STATS.includes(stat as typeof DOH_STATS[number]);
            case "DoL":
                return DOL_STATS.includes(stat as typeof DOL_STATS[number]);
        }
    }

    /**
     * Determine whether a stat can naturally appear on gear. Like {@link isStatRelevant}, but returns false if the
     * stat does not appear on gear for this class. e.g. returns false for DH on tanks and healers.
     *
     * @param stat
     */
    isStatPossibleOnGear(stat: RawStatKey | undefined): boolean {
        const role = this.classJobEarlyStats.combatRole;
        if (stat === 'vitality') {
            return true;
        }
        if (stat === 'dhit' && (role === 'Healer' || role === 'Tank')) {
            return false;
        }
        return this.isStatRelevant(stat);
    }

    /**
     * Determine whether a stat should be shown by default on the custom item UI. Same as {@link isStatPossibleOnGear}.
     *
     * @param stat The stat
     */
    isStatRelevantForCustomItems(stat: RawStatKey | undefined): boolean {
        return this.isStatPossibleOnGear(stat);
    }

    /**
     * Get sims which might be relevant to this sheet.
     */
    get relevantSims() {
        return getRegisteredSimSpecs().filter(simSpec => {
            const jobs = simSpec.supportedJobs;
            // If the sim is jobless (e.g. potency ratio), always display it
            if (jobs === undefined) {
                return true;
            }
            else {
                // Otherwise, make sure there is at least one job overlapping.
                return jobs.find(job => this.allJobs.includes(job)) !== undefined;
            }
        });
    }

    /**
     * Get the currently-selected race's stats.
     */
    get raceStats() {
        return getRaceStats(this._race);
    }

    /**
     * Get the currently-selected race.
     */
    get race() {
        return this._race;
    }

    set race(race) {
        this._race = race;
        this.recalcAll();
    }

    /**
     * Get all sets on this sheet.
     */
    get sets(): CharacterGearSet[] {
        return this._sets;
    }

    /**
     * Get all sims on this sheet.
     */
    get sims(): Simulation<any, any, any>[] {
        return this._sims;
    }

    /**
     * Add a sim to this sheet.
     *
     * @param sim
     */
    addSim(sim: Simulation<any, any, any>) {
        this._sims.push(sim);
        this.saveData();
    }

    /**
     * Delete a sim from this sheet.
     *
     * @param sim
     */
    delSim(sim: Simulation<any, any, any>) {
        this._sims = this._sims.filter(s => s !== sim);
        this.saveData();
    }

    /**
     * Get materia which are relevant to this sheet.
     */
    get relevantMateria(): Materia[] {
        return this._relevantMateria;
    }

    /**
     * Get materia which are relevant within a specific slot. On top of needing to be class-relevant, it must fit
     * in the slot.
     *
     * @param slot
     * @param filterByGrade
     */
    getRelevantMateriaFor(slot: MeldableMateriaSlot | MateriaSlot, filterByGrade: boolean = true): Materia[] {
        const actualSlot: MateriaSlot = 'materiaSlot' in slot ? slot.materiaSlot : slot;
        const materia = this._relevantMateria.filter(mat => mat.ilvl <= actualSlot.ilvl)
            .filter(mat => mat.materiaGrade <= actualSlot.maxGrade)
            .filter(mat => !mat.isHighGrade || actualSlot.allowsHighGrade);
        // Sort materia from highest to lowest
        materia.sort((left, right) => {
            if (left.materiaGrade > right.materiaGrade) {
                return -1;
            }
            else if (left.materiaGrade < right.materiaGrade) {
                return 1;
            }
            return 0;
        });
        if (materia.length === 0) {
            return [];
        }
        // Find highest grade materia
        const maxGrade = materia[0].materiaGrade;
        // If filterByGrade is true, find lowest grade that we want to display - three grades lower. e.g. if the gear can support materia X,
        // then we want to display X, IX for pentamelds, as well as IIX and VIII for budget sets.
        // If filtering is disabled, show everything.
        const minDisplayGrade = filterByGrade ? maxGrade - 3 : 1;
        return materia.filter(mat => mat.materiaGrade >= minDisplayGrade);
    }

    /**
     * Get the current party bonus amount.
     */
    get partyBonus(): PartyBonusAmount {
        return this._partyBonus;
    }

    set partyBonus(partyBonus: PartyBonusAmount) {
        this._partyBonus = partyBonus;
        this.recalcAll();
    }

    /**
     * Get all items which are relevant, and are not filtered out by display settings. Custom items are always displayed
     * regardless of filtering.
     */
    get itemsForDisplay(): GearItem[] {
        const settings = this._itemDisplaySettings;
        return [
            ...this.dataManager.allItems.filter(item => {
                // Special case for FSH
                if (item.displayGearSlotName === 'OffHand'
                    && item.usableByJob('FSH')) {
                    return true;
                }
                return item.ilvl >= settings.minILvl
                    && (item.ilvl <= settings.maxILvl
                        || item.isCustomRelic && settings.higherRelics)
                    && (!item.isNqVersion || settings.showNq);
            }),
            ...this._customItems].filter(item => {
            // TODO: setting to allow showing of hidden items
            return settings.showHidden || !this.isItemHidden(item);
        });
    }

    /**
     * Overridable hook for when gear display settings are updated.
     */
    gearDisplaySettingsUpdateLater() {

    }

    gearDisplaySettingsUpdateNow() {

    }

    /**
     * The item display settings. Uses a proxy which automatically calls {@link gearDisplaySettingsUpdateLater} after
     * any fields are modified.
     */
    get itemDisplaySettings(): ItemDisplaySettings {
        return writeProxy(this._itemDisplaySettings, () => this.gearDisplaySettingsUpdateLater());
    }

    /**
     * Food items which are relevant and pass the filter. Custom foods will be displayed regardless of filtering.
     */
    get foodItemsForDisplay(): FoodItem[] {
        const settings = this._itemDisplaySettings;
        return [...this._dmRelevantFood.filter(item => {
            return item.ilvl >= settings.minILvlFood && item.ilvl <= settings.maxILvlFood
                // Unless the user has opted into showing one-stat-relevant food, only show food with two relevant substats.
                // _dmRelevantFood is already filtered to food which has at least one relevant stat.
                && (settings.showOneStatFood || (this.isStatRelevant(item.primarySubStat) && this.isStatRelevant(item.secondarySubStat)));
        }), ...this._customFoods].filter(item => {
            return settings.showHidden || !this.isItemHidden(item);
        });
    }

    /**
     * Relevant food for the meld/food solver. Will return all of the highest level food with two valid stats and all custom food.
     */
    get relevantFoodForSolver(): FoodItem[] {
        return [...this._dmRelevantFood.filter(item => {
            return item.ilvl >= defaultItemDisplaySettings.minILvlFood
                // Filter to only two relevant substats (i.e. no tenacity for healers)
                && (this.isStatRelevant(item.primarySubStat) && this.isStatRelevant(item.secondarySubStat));
        }), ...this._customFoods];
    }

    /**
     * Get the best possible materia for a particular slot of the given stat.
     *
     * @param stat
     * @param meldSlot
     */
    getBestMateria(stat: MateriaSubstat, meldSlot: MeldableMateriaSlot): Materia | undefined {
        const materiaFilter = (materia: Materia) => {
            return isMateriaAllowed(materia, meldSlot.materiaSlot) && materia.primaryStat === stat;
        };
        const sortedOptions = this.relevantMateria.filter(materiaFilter).sort((first, second) => second.primaryStatValue - first.primaryStatValue);
        return sortedOptions.length >= 1 ? sortedOptions[0] : undefined;
    }

    /**
     * Retrieve a particular materia by its item ID. Returns undefined if the materia does not exist.
     *
     * @param materiaId
     */
    getMateriaById(materiaId: number): Materia | undefined {
        return this.dataManager.materiaById(materiaId);
    }

    /**
     * Add any sims which are considered to be "default" sims for this class and level.
     */
    addDefaultSims() {
        const sims = getDefaultSims(this.classJobName, this.level);
        for (const simSpec of sims) {
            try {
                this.addSim(simSpec.makeNewSimInstance());
            }
            catch (e) {
                console.error(`Error adding default sim ${simSpec.displayName} (${simSpec.stub})`, e);
            }
        }
    }

    /**
     * Recompute custom item stats, and force a recalc of sets.
     *
     * This should be called after custom items are modified.
     */
    recheckCustomItems() {
        for (const customItem of this._customItems) {
            customItem.recheckSync();
        }
        this._sets.forEach(set => set.forceRecalc());
    }

    /**
     * All gear items, not including custom items nor food.
     */
    get allItems(): GearItem[] {
        return this.dataManager.allItems;
    }

    /**
     * All food items, not including custom foods.
     */
    get allFoodItems(): FoodItem[] {
        return this.dataManager.allFoodItems;
    }

    /**
     * Medicine items which are relevant to the current job and pass the item-level filter.
     */
    get medicineItemsForDisplay(): MedicineItem[] {
        const settings = this._itemDisplaySettings;
        return this.dataManager.allMedicineItems.filter(item =>
            item.ilvl >= settings.minILvlFood
            && item.ilvl <= settings.maxILvlFood
            && (this.isStatRelevant(item.primarySubStat) || this.isStatRelevant(item.secondarySubStat))
            && (settings.showHidden || !this.isItemHidden(item))
        );
    }

    /**
     * All medicine items.
     */
    get allMedicineItems(): MedicineItem[] {
        return this.dataManager.allMedicineItems;
    }

    get allMateria(): Materia[] {
        return this.dataManager.allMateria;
    }

    /**
     * Get the consolidated level/ilevel sync info.
     */
    get syncInfo(): SyncInfo {
        const levelSync = this.level === CURRENT_MAX_LEVEL ? null : this.level;
        const explicitIlvlSync = this.ilvlSync ?? null;
        if (explicitIlvlSync !== null) {
            // Explicit ilvl sync
            return {
                lvlSync: levelSync,
                ilvlSync: explicitIlvlSync,
                ilvlSyncIsExplicit: true,
            };
        }
        else {
            if (levelSync !== null) {
                // Implicit ilvl sync
                const implicitSync = this.dataManager.getImplicitIlvlSync(levelSync, true);
                if (implicitSync === undefined) {
                    return {
                        lvlSync: levelSync,
                        ilvlSync: null,
                    };
                }
                else {
                    return {
                        lvlSync: levelSync,
                        ilvlSync: implicitSync,
                        ilvlSyncIsExplicit: false,
                    };
                }
            }
            else {
                return {
                    lvlSync: levelSync,
                    ilvlSync: null,
                };
            }
        }
    }


    get timestamp(): Date {
        return this._timestamp;
    }

    get activeSpecialStat(): SpecialStatType | null {
        return this._activeSpecialStat;
    }

    set activeSpecialStat(value: SpecialStatType | null) {
        this._activeSpecialStat = value;
        this.dataManager.allItems.forEach(item => {
            item.activeSpecialStat = value;
        });
        this.recalcAll();
    }

    checkCompatibility(setA: CharacterGearSet, setB: CharacterGearSet): SetCompatibilityReport {
        const incomp: SlotIncompatibility[] = [];
        const slotsA = new Map<number, EquippedItem[]>();
        const slotsB = new Map<number, EquippedItem[]>();
        EquipSlots.forEach(slot => {
            const itemA = setA.equipment[slot];
            const itemB = setB.equipment[slot];
            if (itemA) {
                const id = itemA.gearItem.id;
                slotsA.set(id, [...(slotsA.get(id) ?? []), itemA]);
            }
            if (itemB) {
                const id = itemB.gearItem.id;
                slotsB.set(id, [...(slotsB.get(id) ?? []), itemB]);
            }
        });
        outer: for (const [id, itemsA] of slotsA) {
            const itemsB = slotsB.get(id) ?? [];
            // Item not equipped in other set - no incompatibility
            if (itemsB.length === 0) {
                continue;
            }
            if (itemsA.length === 1 && itemsB.length === 1) {
                // Simple case for non-ring items
                const compat = checkItemCompat(itemsA[0], itemsB[0]);
                if (compat !== 'compatible') {
                    incomp.push(compat);
                }
            }
            else {
                // Complex cases for rings:
                // If both sets have two of the same ring, then we compare first-to-first, second-to-second, and then
                // first-to-second and second-to-first, keeping whichever combination results in fewer incompatibilities.
                // If one set has two of the same ring while the other set has one, then we compare that one to
                // both and report both (since the user can fix this by making *either* ring compatible).
                if (itemsA.length === 2 && itemsB.length === 2) {
                    const compat00 = checkItemCompat(itemsA[0], itemsB[0]);
                    const compat11 = checkItemCompat(itemsA[1], itemsB[1]);
                    if (compat00 === 'compatible' && compat11 === 'compatible') {
                        // Rings are compatible already
                        continue;
                    }
                    const compat01 = checkItemCompat(itemsA[0], itemsB[1]);
                    const compat10 = checkItemCompat(itemsA[1], itemsB[0]);
                    if (compat01 === 'compatible' && compat10 === 'compatible') {
                        // Rings are fully compatible if we switch left/right ring
                        continue;
                    }
                    if (compat00 === 'compatible' || compat11 === 'compatible') {
                        // Either 00 are compatible or 11. Case where both are compatible is already handled.
                        if (compat00 === 'compatible') {
                            incomp.push(compat11 as SlotIncompatibility);
                        }
                        else {
                            incomp.push(compat00);
                        }
                    }
                    else if (compat01 === 'compatible' || compat10 === 'compatible') {
                        // Either 01 are compatible or 10. Case where both are compatible is already handled.
                        if (compat01 === 'compatible') {
                            incomp.push(compat10 as SlotIncompatibility);
                        }
                        else {
                            incomp.push(compat01);
                        }
                    }
                    else {
                        // Nothing is compatible. Just go with 00 and 11.
                        incomp.push(compat00, compat11);
                    }
                }
                else {
                    let base: EquippedItem;
                    let others: EquippedItem[];
                    if (itemsA.length === 1) {
                        base = itemsA[0];
                        others = itemsB;
                    }
                    else if (itemsB.length === 1) {
                        base = itemsB[0];
                        others = itemsA;
                    }
                    else {
                        // ERROR
                        // TODO
                        throw new Error("TODO this is a bug");
                    }
                    const possibilities: SlotIncompatibility[] = [];
                    for (const other of others) {
                        const compat = checkItemCompat(base, other);
                        if (compat === 'compatible') {
                            continue outer;
                        }
                        possibilities.push(compat);
                    }
                    incomp.push(...possibilities);
                }
            }
        }
        return new SetCompatibilityReport(setA, setB, incomp);
    }

    applicableSpecialStat(): SpecialStatKey | null {
        if (!this.ilvlSync) {
            return null;
        }
        for (const specialstatkey of SPECIAL_STAT_KEYS) {
            const mapping = SPECIAL_STATS_MAPPING[specialstatkey];
            if (mapping && mapping.level === this.level && mapping.ilvls.includes(this.ilvlSync)) {
                return specialstatkey;
            }
        }
        return null;
    }

    isItemHidden(item: GearItem | FoodItem | MedicineItem): boolean {
        return this.hiddenItems.has(item.id);
    }

    setItemHidden(item: GearItem | FoodItem | MedicineItem, isHidden: boolean) {
        if (isHidden) {
            this.hiddenItems.add(item.id);
        }
        else {
            this.hiddenItems.delete(item.id);
        }
        // TODO: this is inefficient because it causes all of the gear tables to refresh, not just the one that we
        // actually hid an item in.
        this.gearDisplaySettingsUpdateNow();
    }

    resetHiddenItems() {
        this.hiddenItems.clear();
        this.gearDisplaySettingsUpdateNow();
    }
}

function checkItemCompat(itemA: EquippedItem, itemB: EquippedItem): SlotIncompatibility | 'compatible' {

    if (itemA.gearItem.id !== itemB.gearItem.id) {
        return 'compatible';
    }
    const slot = itemA.gearItem.displayGearSlotName;
    if (itemA.gearItem.isCustomRelic) {
        // Dealing with relics
        const badSubStats: string[] = [];
        ALL_COMBAT_SUB_STATS.forEach(stat => {
            const statValueA = itemA.relicStats[stat];
            const statValueB = itemB.relicStats[stat];
            if (statValueA !== statValueB) {
                badSubStats.push(`${stat}: ${statValueA} ≠ ${statValueB}`);
            }
        });
        if (badSubStats.length > 0) {
            return {
                slotKey: slot,
                itemA: itemA,
                itemB: itemB,
                reason: 'relic-stat-mismatch',
                subIssues: badSubStats,
                hardBlocker: true,
            };
        }
    }
    else {
        // Dealing with materia slots
        const numMeldSlots = Math.max(itemA.melds.length, itemB.melds.length);
        const issues: string[] = [];
        for (let i = 0; i < numMeldSlots; i++) {
            const slotNum = i + 1;
            const meldA = itemA.melds[i]?.equippedMateria;
            const meldB = itemB.melds[i]?.equippedMateria;
            const descA = meldA ? materiaShortLabel(meldA) : 'empty';
            const descB = meldB ? materiaShortLabel(meldB) : 'empty';
            if (meldA?.id !== meldB?.id) {
                issues.push(`Slot ${slotNum}: ${descA} ≠ ${descB}`);
            }
        }
        if (issues.length > 0) {
            return {
                slotKey: slot,
                itemA: itemA,
                itemB: itemB,
                reason: 'materia-mismatch',
                subIssues: issues,
                // If the item is not unique, then it's not a hard blocker since you could buy multiple of
                // the item and fill them with different materia.
                hardBlocker: itemA.gearItem.isUnique,
            };
        }
    }
    return 'compatible';
}

/**
 * Describes the level of compatibility between two sets.
 *
 * compatible: Any common items in both sets have the same melds or custom relic stats.
 *
 * soft-incompatible: One or more items use different melds/stats between the two sets, but the items in question
 * are not unique. You can still assemble both sets, but you will need to buy/craft duplicate items.
 *
 * hard-incompatible: One or more items use different melds/stats between the two sets, and at least one such item
 * is unique. It will not be possible to assemble both sets.
 */
export type SetCompatibilityLevel = 'compatible' | 'soft-incompatible' | 'hard-incompatible';

/**
 * Describes the (in)compatibility between two gear sets.
 */
export class SetCompatibilityReport {
    constructor(readonly setA: CharacterGearSet, readonly setB: CharacterGearSet, readonly incompatibleSlots: SlotIncompatibility[]) {
    }

    get compatibilityLevel(): SetCompatibilityLevel {
        if (this.incompatibleSlots.length === 0) {
            return 'compatible';
        }
        else {
            return this.incompatibleSlots.some(incomp => incomp.hardBlocker) ? 'hard-incompatible' : 'soft-incompatible';
        }
    }
}

/**
 * Describes a slot incompatibility between two gear sets.
 */
export type SlotIncompatibility = {
    slotKey: DisplayGearSlotKey;
    itemA: EquippedItem;
    itemB: EquippedItem;
    reason: SlotIncompatibilityReason;
    /**
     * Human-readable details about the incompatibility.
     */
    subIssues: string[];
    /**
     * Whether this is a hard blocker. If true, it means that the item is not unique, and you cannot assemble both sets.
     * If false, you can work around it using duplicate items.
     */
    hardBlocker: boolean;
}

/**
 * Describes the reason why two slots might be incompatible.
 */
export type SlotIncompatibilityReason = 'materia-mismatch' | 'relic-stat-mismatch';

export type SheetExportOptions = {
    includeStats: boolean;
    includeSims: SimExportMode;
    includeSaveKey: boolean;
    /**
     * Whether to include flags such as items that are hidden, or (future state) items that are intended for inclusion
     * in the gear solver possibilities.
     */
    includeItemFlags: boolean;
}

// TODO: make ternaries for the rest of the combinations
type ExportTypeFor<X extends SheetExportOptions> = X extends {
    includeStats: true
} ? SheetStatsExport : SheetExport;

export type SimExportMode = 'selected-only' | 'all' | 'none'

/**
 * Export options for the meld solver. The meld solver needs basically nothing, since it
 * computes the stats on its own, does not save, and we send the sim over separately.
 */
export const SolverExport = {
    includeStats: false,
    includeSims: 'none',
    includeSaveKey: false,
    includeItemFlags: true,
} as const satisfies SheetExportOptions;

/**
 * Export options for a typical external export. We do not include a save key, and include only
 * the sims that the user selected, and do not include stats.
 */
const ExternalExport = {
    includeStats: false,
    includeSims: 'selected-only',
    includeSaveKey: false,
    includeItemFlags: false,
} as const satisfies SheetExportOptions;

/**
 * Export options for a typical internal save. We include the save key, and all sims.
 */
const InternalSave = {
    includeStats: false,
    includeSims: 'all',
    includeSaveKey: true,
    includeItemFlags: true,
} as const satisfies SheetExportOptions;

/**
 * Export options for a typical internal save-as. Same as a normal save, but we do not need
 * the save key since we will replace it with a new one anyway.
 */
const InternalSaveAs = {
    includeStats: false,
    includeSims: 'all',
    includeSaveKey: false,
    includeItemFlags: true,
} as const satisfies SheetExportOptions;

/**
 * Export options for the fulldata endpoint. Include stats and sims, but not a save key.
 */
const FullStatsExport = {
    includeStats: true,
    includeSims: 'all',
    includeSaveKey: false,
    includeItemFlags: true,
} as const satisfies SheetExportOptions;

export const ExportTypes = {
    SolverExport,
    ExternalExport,
    InternalSave,
    InternalSaveAs,
    FullStatsExport,
} as const;

function jobDataSerializationProxy(jobStats: JobData): JobDataExport {
    return {
        type: jobStats.type,
        combatRole: jobStats.combatRole,
        mainStat: jobStats.mainStat,
        secondaryStat: jobStats.secondaryStat,
        autoAttackStat: jobStats.autoAttackStat,
        irrelevantSubstats: jobStats.irrelevantSubstats,
        offhand: jobStats.offhand,
        // Deprecated, keep dummy value here to keep consumers happy
        meldParamIndex: 0,
        aaPotency: jobStats.aaPotency,
        excludedRelicSubstats: jobStats.excludedRelicSubstats,
        minLevel: jobStats.minLevel,
        maxLevel: jobStats.maxLevel,
        jobStatMultipliers: jobStats.jobStatMultipliers,
    } satisfies JobDataExport;
}

/**
 * Transform a ComputedSetStats into a form that serializes properly. That is, it serializes the getters rather
 * than only the backing data. This is realistically what you would want out of the fulldata API endpoint.
 *
 * @param stats
 */
export function statsSerializationProxy(stats: ComputedSetStats): ComputedSetStatsExport {
    // The purpose of this is that the fullstats API won't correctly serialize the ComputedSetStatsImpl normally.
    return new Proxy(stats, {
        get(target, prop, receiver) {
            if (prop === 'jobStats') {
                return jobDataSerializationProxy(target.jobStats);
            }
            // Check if the property is a getter on the prototype chain
            let descriptor = Object.getOwnPropertyDescriptor(target, prop as string);
            let proto = Object.getPrototypeOf(target);

            while (!descriptor && proto) {
                descriptor = Object.getOwnPropertyDescriptor(proto, prop as string);
                proto = Object.getPrototypeOf(proto);
            }

            if (descriptor && typeof descriptor.get === 'function') {
                return descriptor.get.call(target);
            }

            return Reflect.get(target, prop, receiver);
        },
        ownKeys(target) {
            const keys = new Set<string | symbol>();

            let obj: object = target;
            while (obj) {
                Reflect.ownKeys(obj).forEach((key) => {
                    if (typeof key === 'string' && !key.startsWith('_')) {
                        const descriptor = Object.getOwnPropertyDescriptor(obj, key);
                        if (descriptor && typeof descriptor.get === 'function') {
                            keys.add(key);
                        }
                    }
                });
                obj = Object.getPrototypeOf(obj);
            }

            return Array.from(keys);
        },
        getOwnPropertyDescriptor(target, prop) {
            const descriptor = Object.getOwnPropertyDescriptor(target, prop)
                || Object.getOwnPropertyDescriptor(Object.getPrototypeOf(target), prop);

            if (descriptor
                && typeof descriptor.get === 'function'
                && typeof prop === 'string'
                && !prop.startsWith('_')) {
                return {
                    enumerable: true,
                    configurable: true,
                };
            }
            return undefined;
        },
    }) as unknown as ComputedSetStatsExport;
}
